/**
 * Туры по сайту: карта главной + главы по смыслам (ученик) и карточка ребёнка (родитель).
 *
 * Шаг:
 *   selector  — CSS-якорь [data-tour=...]
 *   path      — куда перейти перед показом (если задан)
 *   pathMatch — (pathname) => bool; если нет — сравниваем path
 *   optional  — пропустить, если якоря нет после ожидания
 *   resolve   — 'first-task-work' | 'first-parent-child' — найти ссылку на странице resolveFrom
 *   resolveFrom — путь, где искать ссылку
 */

export const TOUR_REPLAY_SESSION_KEY = 'diobuch.tour.replay';

/** Старые ключи «весь тур» → считаем карту пройденной. */
const LEGACY_STUDENT_KEY = 'diobuch.tour.student.v1';
const LEGACY_PARENT_KEY = 'diobuch.tour.parent.v1';

export const STUDENT_CHAPTERS = [
  {
    id: 'map',
    role: 'student',
    title: 'Карта сайта',
    blurb: 'Главная и основные разделы',
    storageKey: 'diobuch.tour.student.map.v1',
    autoStart: true,
    homeOnly: true,
    steps: [
      {
        id: 'welcome',
        selector: '[data-tour="student-welcome"]',
        path: '/',
        title: 'Главная',
        body: 'Это ваша главная. Разделы — плашками ниже, те же пункты есть в меню сверху.',
      },
      {
        id: 'schedule',
        selector: '[data-tour="schedule"]',
        path: '/',
        title: 'Расписание',
        body: 'Когда занятия. Рядом журнал: что было на уроке и домашнее.',
      },
      {
        id: 'homework',
        selector: '[data-tour="homework"]',
        path: '/',
        title: 'Списки задач',
        body: 'То, что задал преподаватель. Тесты и тренажёры — рядом, для самостоятельной практики.',
      },
      {
        id: 'konspekt',
        selector: '[data-tour="konspekt"]',
        path: '/',
        title: 'Конспект',
        body: 'Теория по темам. Статьи — отдельные разборы.',
      },
      {
        id: 'more',
        selector: '[data-tour="student-more"]',
        path: '/',
        title: 'Ещё',
        body: 'Прогресс по темам, попытки, каталог и совместное кодирование на уроке.',
        optional: true,
      },
      {
        id: 'stats',
        selector: '[data-tour="student-stats"]',
        path: '/',
        title: 'Статистика',
        body: 'Сколько задач принято. Подробности — в «Мои попытки».',
      },
      {
        id: 'chapters',
        selector: '[data-tour="tour-chapters"]',
        path: '/',
        title: 'Другие туры',
        body: 'Ниже — короткие туры по смыслу: как решать самому, урок, оплата и журнал. Их можно пройти в любой момент.',
        optional: true,
      },
    ],
  },
  {
    id: 'self',
    role: 'student',
    title: 'Сам на сайте',
    blurb: 'Списки, задача, попытки, тренажёры, конспект',
    storageKey: 'diobuch.tour.student.self.v1',
    steps: [
      {
        id: 'lists',
        selector: '[data-tour="tasks-page"]',
        path: '/tasks',
        title: 'Списки задач',
        body: 'Сюда попадают домашние и свои списки. Вкладки «В работе» / «Завершён» — что ещё не сдано.',
      },
      {
        id: 'catalog',
        selector: '[data-tour="catalog-page"]',
        path: '/catalog',
        title: 'Каталог',
        body: 'Банк задач для самостоятельной практики. Можно искать и открыть задачу в редакторе.',
      },
      {
        id: 'work-open',
        selector: '[data-tour="work-page"]',
        resolve: 'first-task-work',
        resolveFrom: '/tasks',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Рабочее место',
        body: 'Здесь решаете задачу: условие, код или ответ, вывод. Блоки можно двигать (об этом — в туре «Урок»).',
        optional: true,
      },
      {
        id: 'work-check',
        selector: '[data-tour="work-check"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Проверить ответ',
        body: 'Если у задачи есть короткий ответ — кнопка «Проверить». «Показать» открывает эталон ответа.',
        optional: true,
      },
      {
        id: 'work-solution',
        selector: '[data-tour="work-solution"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Решение и разбор',
        body: 'Если преподаватель добавил разбор — «Показать решение / разбор». Не у всех задач он есть.',
        optional: true,
      },
      {
        id: 'work-save',
        selector: '[data-tour="work-save"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Сохранить попытку',
        body: 'Дискета фиксирует попытку (код, запуск, вывод). Черновик сохраняется сам — подробности в «?».',
        optional: true,
      },
      {
        id: 'work-help',
        selector: '[data-tour="work-save-help"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Черновик и попытка',
        body: 'Нажмите «?», чтобы ещё раз прочитать разницу между авточерновиком и записью в историю.',
        optional: true,
      },
      {
        id: 'attempts',
        selector: '[data-tour="attempts-page"]',
        path: '/my-attempts',
        title: 'Мои попытки',
        body: 'Здесь все сохранённые попытки. Можно открыть задачу и вернуться к коду.',
      },
      {
        id: 'trainers',
        selector: '[data-tour="trainers-page"]',
        path: '/trainers',
        title: 'Тренажёры',
        body: 'Короткие тренажёры по темам. «Настроить» — параметры, затем проверка ответа.',
      },
      {
        id: 'konspekty',
        selector: '[data-tour="konspekty-page"]',
        path: '/konspekty',
        title: 'Конспекты',
        body: 'Теория по курсам. Внутри статей бывает код с кнопкой «Запустить» — можно потрогать пример.',
      },
    ],
  },
  {
    id: 'lesson',
    role: 'student',
    title: 'Урок',
    blurb: 'Вход в урок, блоки, отчёт, сдача списка',
    storageKey: 'diobuch.tour.student.lesson.v1',
    steps: [
      {
        id: 'lesson-nav',
        selector: '[data-tour="student-welcome"]',
        path: '/',
        title: 'Ссылка «Урок»',
        body: 'Когда идёт занятие, в меню сверху появляется «Урок» (иногда ещё «Телемост»). Без активного урока пункта нет — это нормально.',
      },
      {
        id: 'lesson-live',
        selector: '[data-tour="lesson"]',
        path: '/',
        title: 'Урок идёт',
        body: 'Сейчас занятие активно — откройте эту плашку или пункт меню, чтобы попасть в список урока.',
        optional: true,
      },
      {
        id: 'lesson-lists',
        selector: '[data-tour="tasks-page"]',
        path: '/tasks',
        title: 'Список на уроке',
        body: 'Домашние и урочные списки здесь. На карточке есть «Сдать список», когда закончили.',
      },
      {
        id: 'lesson-finish-card',
        selector: '[data-tour="tasks-finish"]',
        path: '/tasks',
        title: 'Сдать список',
        body: 'Кнопка на карточке списка: сдать преподавателю или вернуть в работу.',
        optional: true,
      },
      {
        id: 'lesson-work',
        selector: '[data-tour="work-page"]',
        resolve: 'first-task-work',
        resolveFrom: '/tasks',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Экран задачи на уроке',
        body: 'Тот же редактор, что при самостоятельной работе. На уроке преподаватель может смотреть ваш код вместе с вами.',
        optional: true,
      },
      {
        id: 'lesson-layout',
        selector: '[data-tour="work-layout"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Блоки ⊞',
        body: 'Кнопка ⊞ включает режим: блоки можно перетаскивать и менять размер. A и B — готовые раскладки.',
        optional: true,
      },
      {
        id: 'lesson-report',
        selector: '[data-tour="work-report"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Отчёт по задаче',
        body: 'В списке задач — «Отчёт»: самооценка (++ / + / − …). Это тоже сохраняет попытку.',
        optional: true,
      },
      {
        id: 'lesson-finish-work',
        selector: '[data-tour="work-finish-list"]',
        pathMatch: (p) => /^\/work\//.test(p) || /\/sessions\/[^/]+\/tasks\//.test(p),
        title: 'Сдать из редактора',
        body: 'Внизу блока «Список задач» — та же сдача списка, не уходя со страницы.',
        optional: true,
      },
    ],
  },
  {
    id: 'pay',
    role: 'student',
    title: 'Оплата',
    blurb: 'Баланс, подтверждение занятий, пополнение',
    storageKey: 'diobuch.tour.student.pay.v1',
    steps: [
      {
        id: 'billing-page',
        selector: '[data-tour="billing-page"]',
        path: '/billing',
        title: 'Баланс и оплата',
        body: 'Семейный счёт: долг, предоплата и история. Тот же экран видит родитель.',
      },
      {
        id: 'billing-facts',
        selector: '[data-tour="billing-facts"]',
        path: '/billing',
        title: 'Цифры',
        body: 'Текущий баланс и сколько уже внесено.',
      },
      {
        id: 'billing-pending',
        selector: '[data-tour="billing-pending"]',
        path: '/billing',
        title: 'Подтвердите занятия',
        body: 'После урока преподаватель выставляет отчёт. Подтверждение списывает сумму со счёта.',
        optional: true,
      },
      {
        id: 'billing-topup',
        selector: '[data-tour="billing-topup"]',
        path: '/billing',
        title: 'Пополнить',
        body: 'Укажите сумму и перейдите к оплате. Если блока нет — пополнение временно недоступно.',
        optional: true,
      },
    ],
  },
  {
    id: 'journal',
    role: 'student',
    title: 'Журнал',
    blurb: 'Занятия и блок «Сейчас сделать»',
    storageKey: 'diobuch.tour.student.journal.v1',
    steps: [
      {
        id: 'journal-page',
        selector: '[data-tour="journal-page"]',
        path: '/my-lessons',
        title: 'Журнал занятий',
        body: 'История уроков: что делали, домашнее задание и ваши комментарии к отчёту.',
      },
      {
        id: 'journal-todo',
        selector: '[data-tour="journal-todo"]',
        path: '/my-lessons',
        title: 'Сейчас сделать',
        body: 'Текущая домашка сверху списка. Если блока нет — сейчас нечего делать или ДЗ ещё не выдали.',
        optional: true,
      },
      {
        id: 'journal-list',
        selector: '[data-tour="journal-list"]',
        path: '/my-lessons',
        title: 'Записи занятий',
        body: 'По каждому занятию — отчёт, ДЗ и ссылки на задачи. Можно листать вниз.',
        optional: true,
      },
    ],
  },
];

export const PARENT_CHAPTERS = [
  {
    id: 'map',
    role: 'parent',
    title: 'Кабинет родителя',
    blurb: 'Главная: семья, оплата, дети',
    storageKey: 'diobuch.tour.parent.map.v1',
    autoStart: true,
    homeOnly: true,
    steps: [
      {
        id: 'welcome',
        selector: '[data-tour="parent-welcome"]',
        path: '/parent',
        title: 'Кабинет родителя',
        body: 'Кабинет родителя: занятия, оплата и как дела у ребёнка. Написать преподавателю — в сообщениях.',
      },
      {
        id: 'summary',
        selector: '[data-tour="parent-summary"]',
        path: '/parent',
        title: 'Семья',
        body: 'Счёт, ближайшее занятие и непрочитанные. После урока приходит отчёт: его нужно подтвердить, тогда сумма спишется со счёта.',
      },
      {
        id: 'pending',
        selector: '[data-tour="parent-pending"]',
        path: '/parent',
        title: 'Подтвердите занятия',
        body: 'Эти занятия ещё не подтверждены.',
        optional: true,
      },
      {
        id: 'invite',
        selector: '[data-tour="parent-invite"]',
        path: '/parent',
        title: 'Пригласить ребёнка',
        body: 'Если ребёнка ещё нет, создайте ссылку и отправьте её ученику. Либо откройте ссылку, которую прислал он.',
        optional: true,
      },
      {
        id: 'children',
        selector: '[data-tour="parent-children"]',
        path: '/parent',
        title: 'Дети',
        body: 'Ближайшее и последнее занятие, был ли ребёнок на сайте. «Подробнее» — прогресс и анкета.',
      },
      {
        id: 'chapters',
        selector: '[data-tour="tour-chapters"]',
        path: '/parent',
        title: 'Ещё один тур',
        body: 'Ниже можно пройти тур по карточке ребёнка: занятия, оплата, темы и активность.',
        optional: true,
      },
    ],
  },
  {
    id: 'child',
    role: 'parent',
    title: 'Карточка ребёнка',
    blurb: 'Занятия, оплата, темы, активность',
    storageKey: 'diobuch.tour.parent.child.v1',
    steps: [
      {
        id: 'pick',
        selector: '[data-tour="parent-children"]',
        path: '/parent',
        title: 'Откройте ребёнка',
        body: 'Нажмите «Подробнее» у ребёнка — или продолжите: тур откроет карточку сам, если дети уже привязаны.',
      },
      {
        id: 'child-page',
        selector: '[data-tour="parent-child-page"]',
        resolve: 'first-parent-child',
        resolveFrom: '/parent',
        pathMatch: (p) => /^\/parent\/children\//.test(p),
        title: 'Карточка',
        body: 'Журнал занятий, оплата по ребёнку, темы, активность на сайте и анкета.',
        optional: true,
      },
      {
        id: 'child-tabs',
        selector: '[data-tour="parent-child-tabs"]',
        pathMatch: (p) => /^\/parent\/children\//.test(p),
        title: 'Вкладки',
        body: 'Переключайтесь между занятиями, оплатой, темами, активностью и анкетой.',
        optional: true,
      },
      {
        id: 'child-lessons',
        selector: '[data-tour="parent-child-panel"]',
        pathMatch: (p) => /^\/parent\/children\//.test(p),
        title: 'Занятия',
        body: 'Что было на уроках: даты, отчёты и домашнее. Это тот же журнал, что видит ученик.',
        optional: true,
      },
      {
        id: 'child-activity',
        selector: '[data-tour="parent-child-tabs"]',
        resolve: 'parent-child-tab-activity',
        pathMatch: (p) => /^\/parent\/children\//.test(p),
        title: 'Активность',
        body: 'Вкладка «Активность» — что ребёнок решал на сайте (без кода): самооценка и комментарии преподавателя.',
        optional: true,
      },
      {
        id: 'child-payment',
        selector: '[data-tour="parent-child-tabs"]',
        resolve: 'parent-child-tab-payment',
        pathMatch: (p) => /^\/parent\/children\//.test(p),
        title: 'Оплата по ребёнку',
        body: 'Вкладка «Оплата» — списания и подтверждения по этому ученику. Общий баланс семьи — в меню «Баланс».',
        optional: true,
      },
    ],
  },
];

export function getTourRole(user) {
  if (!user) return null;
  const isTeacher = Boolean(user.is_teacher || user.is_staff || user.is_admin);
  if (isTeacher && !user.is_student) return null;
  if (user.is_parent && !user.is_student && !isTeacher) return 'parent';
  if (user.is_student) return 'student';
  return null;
}

export function getTourHomePath(role) {
  if (role === 'parent') return '/parent';
  if (role === 'student') return '/';
  return null;
}

export function isTourHomePath(role, pathname) {
  if (role === 'parent') return pathname === '/parent';
  if (role === 'student') return pathname === '/' || pathname === '/dashboard';
  return false;
}

export function getChaptersForRole(role) {
  if (role === 'parent') return PARENT_CHAPTERS;
  if (role === 'student') return STUDENT_CHAPTERS;
  return [];
}

export function getChapter(role, chapterId) {
  return getChaptersForRole(role).find((c) => c.id === chapterId) || null;
}

export function getAutoStartChapter(role) {
  return getChaptersForRole(role).find((c) => c.autoStart) || null;
}

function legacyDone(role) {
  try {
    const key = role === 'student' ? LEGACY_STUDENT_KEY : role === 'parent' ? LEGACY_PARENT_KEY : null;
    return key ? window.localStorage.getItem(key) === '1' : false;
  } catch {
    return false;
  }
}

export function readChapterDone(role, chapterId) {
  const chapter = getChapter(role, chapterId);
  if (!chapter) return true;
  try {
    if (window.localStorage.getItem(chapter.storageKey) === '1') return true;
    if (chapter.id === 'map' && legacyDone(role)) return true;
    return false;
  } catch {
    return true;
  }
}

export function writeChapterDone(role, chapterId) {
  const chapter = getChapter(role, chapterId);
  if (!chapter) return;
  try {
    window.localStorage.setItem(chapter.storageKey, '1');
  } catch {
    /* ignore */
  }
}

/** @deprecated alias for map chapter */
export function readTourDone(role) {
  return readChapterDone(role, 'map');
}

/** @deprecated */
export function writeTourDone(role) {
  writeChapterDone(role, 'map');
}

export function requestTourReplay(role, chapterId = 'map') {
  try {
    window.sessionStorage.setItem(TOUR_REPLAY_SESSION_KEY, `${role}:${chapterId}`);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent('diobuch-tour-replay', { detail: { role, chapterId } }),
  );
}

export function consumeTourReplay() {
  try {
    const raw = window.sessionStorage.getItem(TOUR_REPLAY_SESSION_KEY);
    if (raw) window.sessionStorage.removeItem(TOUR_REPLAY_SESSION_KEY);
    if (!raw) return null;
    if (raw === 'student' || raw === 'parent') {
      return { role: raw, chapterId: 'map' };
    }
    const [role, chapterId] = raw.split(':');
    if ((role === 'student' || role === 'parent') && chapterId) {
      return { role, chapterId };
    }
    return null;
  } catch {
    return null;
  }
}

export function hasCookieConsent() {
  try {
    const v = window.localStorage.getItem('diobuch_cookie_consent_v1');
    return v === 'analytics' || v === 'necessary';
  } catch {
    return false;
  }
}

export function pathMatchesStep(step, pathname) {
  if (typeof step.pathMatch === 'function') return step.pathMatch(pathname);
  if (!step.path) return true;
  if (step.path === '/') return pathname === '/' || pathname === '/dashboard';
  return pathname === step.path || pathname.startsWith(`${step.path}/`);
}

/** Ссылка на первую задачу из списка / каталога. */
export function findFirstTaskWorkHref() {
  const fromTour = document.querySelector('[data-tour-open-work]');
  if (fromTour?.getAttribute('href')) return fromTour.getAttribute('href');
  const fromList = document.querySelector('a.task-list-title-link');
  if (fromList?.getAttribute('href')) return fromList.getAttribute('href');
  const fromCatalog = document.querySelector('a.catalog-row-title[href^="/work/"]');
  if (fromCatalog?.getAttribute('href')) return fromCatalog.getAttribute('href');
  return null;
}

export function findFirstParentChildHref() {
  const el = document.querySelector('[data-tour-open-child]');
  if (el?.getAttribute('href')) return el.getAttribute('href');
  const link = document.querySelector('a[href^="/parent/children/"]');
  return link?.getAttribute('href') || null;
}

/** Совместимость со старым API. */
export function getTourSteps(role) {
  const map = getChapter(role, 'map');
  return map?.steps || [];
}
